/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import './addEditDialog.scss';
import './zohoUploadDialog.scss';

const ACCEPTED_EXTENSIONS = ['csv'];
const MAX_FILE_SIZE_MB = 10;

const ZohoUploadDialog = ({ open, onClose, baseURL, headers, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [serverReport, setServerReport] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setSelectedFile(null);
    setFileError('');
    setServerReport(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validateFile = (file) => {
    if (!file) return 'Please select a file.';
    const ext = file.name.split('.').pop()
      .toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}.`;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size must be under ${MAX_FILE_SIZE_MB}MB.`;
    }
    return '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validateFile(file);
    setFileError(error);
    setSelectedFile(error ? null : file);
    // Reset input so same file can be re-selected after error
    e.target.value = '';
  };

  const handleUpload = async () => {
    const error = validateFile(selectedFile);
    if (error) {
      setFileError(error);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    setUploading(true);
    setServerReport(null); // Clear previous reports

    try {
      const res = await fetch(`${baseURL}/upload`, {
        method: 'POST',
        headers: { ...headers },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        if (data?.data) {
          setServerReport(data.data);
        }

        toast.error(data?.message || 'Import completed with errors.');
        return;
      }

      toast.success(data?.message || 'File uploaded successfully.');
      handleClose();
      onSuccess?.();

    } catch (err) {
      toast.error('Upload failed. Please check your connection.');
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = async () => {
    try {
      const response = await fetch(`${baseURL}/get-sample-file`, {
        method: 'GET',
        headers: { ...headers },
      });

      if (!response.ok) {
        throw new Error('Failed to download sample file.');
      }

      const data = await response.json();

      const signedUrl = data?.data?.signedUrl;

      if (!signedUrl) {
        throw new Error('Download URL not found.');
      }

      // Directly use the signed URL - it already has Content-Disposition header
      // No need to fetch and create blob, just trigger the download
      const link = document.createElement('a');
      link.href = signedUrl;
      link.style.display = 'none';

      document.body.append(link);
      link.click();

      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);

    } catch (err) {
      toast.error(err?.message || 'Failed to download sample file.');
    }
  };

  const dialogHeader = (
    <span className="p-dialog-title">Upload Zoho Invoice Details</span>
  );

  const dialogFooter = (
    <div className="zoho-upload-footer">
      <Button
        label="Cancel"
        className="p-button p-button-text"
        onClick={handleClose}
        disabled={uploading}
      />
      <Button
        label={uploading ? 'Uploading…' : 'Upload'}
        className="p-button p-button-add"
        onClick={handleUpload}
        disabled={uploading || !selectedFile}
        loading={uploading}
      />
    </div>
  );

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={dialogHeader}
      footer={dialogFooter}
      className="zoho-invoice-dialog"
      style={{ width: '480px' }}
      modal
      draggable={false}
      resizable={false}
    >
      <div className="zoho-upload-body">
        <div className="zoho-upload-field-label">
          Select file to upload
          <span className="required-asterisk">*</span>
        </div>

        <div className="zoho-upload-file-row">
          <Button
            type="button"
            label="Choose file"
            className="p-button zoho-upload-choose-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <span className="zoho-upload-filename">
            {selectedFile ? selectedFile.name : 'No file chosen'}
          </span>
          <button
            type="button"
            onClick={downloadSample}
            className="zoho-upload-sample-link"
          >
            Sample File
          </button>
        </div>

        {fileError && (
          <div className="zoho-upload-error">{fileError}</div>
        )}

        <div className="zoho-upload-hint">
          Accepted formats: {ACCEPTED_EXTENSIONS.join(', ')} &nbsp;|&nbsp; Max size: {MAX_FILE_SIZE_MB}MB
        </div>

        {/* Add this inside the zoho-upload-body div */}
        {serverReport && (
          <div className="zoho-error-report-container">
            <div className="zoho-error-header">Upload Issues:</div>
            <ul className="zoho-error-list">
              {serverReport.validationErrors?.map((v, i) => (
                <li key={i}>{`Row ${v.row}: ${v.error}`}</li>
              ))}
              {serverReport.duplicates?.map((d, i) => (
                <li key={i}>{`Row ${d.row}: Duplicate Invoice #${d.number}`}</li>
              ))}
              {serverReport.invalidClients?.map((c, i) => (
                <li key={i}>{`Row ${c.row}: Client ${c.name} not found`}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
};

ZohoUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseURL: PropTypes.string.isRequired,
  headers: PropTypes.object,
  onSuccess: PropTypes.func,
};

ZohoUploadDialog.defaultProps = {
  headers: {},
  onSuccess: null,
};

export default ZohoUploadDialog;
